import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { translate } from '@utils/i18n';
import TypeSelect from './TypeSelect';
import SubtypeSelect from './SubtypeSelect';

const namespace = 'features.valves.type_picker';

// Reusable cascading valve type -> subtype picker embedded in server-rendered
// (ERB form_for) device forms via react_component. Mirrors the plain
// .jsx + PropTypes + Bootstrap style of Settings/ValveSubtypes/Index.jsx
// (not the @ui/TypeScript stack) so it doesn't clash with the legacy Bootstrap
// forms it's mounted inside. Syncs hidden <input>s so the surrounding
// form_for submits `{paramName}[valve_type_id]` / `{paramName}[valve_subtype_id]`;
// the ValveTypeAssignable model concern (task-06) dual-writes the legacy enum
// server-side, so no legacy enum field should be submitted alongside this.
function ValveTypePicker({
  paramName,
  instanceId,
  valveTypes,
  initialTypeId,
  initialSubtypeId,
  label,
  typePrompt,
  subtypeLabel,
  subtypePrompt
}) {
  const [typeId, setTypeId] = useState(initialTypeId);
  const [subtypeId, setSubtypeId] = useState(initialSubtypeId);

  const selectedType = valveTypes.find((valveType) => valveType.id === typeId) || null;
  const subtypes = selectedType ? selectedType.valve_subtypes : [];

  // `instanceId` disambiguates label/input `id`s when multiple pickers render
  // on one page (e.g. task-15's mass-edit spreadsheet, one row per device).
  const idPrefix = instanceId ? `${paramName}-${instanceId}` : paramName;

  const handleTypeChange = (newTypeId) => {
    setTypeId(newTypeId);
    setSubtypeId(null);
  };

  // None of the ERB call sites (valves/dry_valves/inspectors_test_valves
  // new/edit views, and building_dry_valves) pass label/typePrompt/
  // subtypeLabel/subtypePrompt explicitly -- they all rely on these
  // defaults, so the translate() lookup happens here (render time) rather
  // than in defaultProps, which must stay static.
  const resolvedLabel = label ?? translate('type_label', { namespace });
  const resolvedTypePrompt = typePrompt ?? translate('type_prompt', { namespace });
  const resolvedSubtypeLabel = subtypeLabel ?? translate('subtype_label', { namespace });
  const resolvedSubtypePrompt = subtypePrompt ?? translate('subtype_prompt', { namespace });

  return (
    <div className="valve-type-picker">
      <TypeSelect
        htmlId={`${idPrefix}-valve-type`}
        valveTypes={valveTypes}
        typeId={typeId}
        typeLabel={resolvedLabel}
        typePrompt={resolvedTypePrompt}
        onChange={handleTypeChange}
      />

      <SubtypeSelect
        htmlId={`${idPrefix}-valve-subtype`}
        subtypes={subtypes}
        subtypeId={subtypeId}
        required={!!selectedType?.require_subtype}
        subtypeLabel={resolvedSubtypeLabel}
        subtypePrompt={resolvedSubtypePrompt}
        onChange={setSubtypeId}
      />

      <input type="hidden" name={`${paramName}[valve_type_id]`} value={typeId === null ? '' : typeId} />
      <input type="hidden" name={`${paramName}[valve_subtype_id]`} value={subtypeId === null ? '' : subtypeId} />
    </div>
  );
}

ValveTypePicker.propTypes = {
  /** Rails param namespace for the device form: 'valve' | 'dry_valve' | 'inspectors_test_valve'. */
  paramName: PropTypes.oneOf(['valve', 'dry_valve', 'inspectors_test_valve']).isRequired,
  /** Optional per-row identifier to keep element ids unique when multiple pickers render on one page (e.g. mass-edit). */
  instanceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Catalog types for the device's category, from ValvesHelper#catalog_valve_types_for_picker. */
  valveTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      type_name: PropTypes.string.isRequired,
      require_subtype: PropTypes.bool.isRequired,
      valve_subtypes: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          type_name: PropTypes.string.isRequired
        })
      ).isRequired
    })
  ).isRequired,
  /** Preselected type id for edit mode; null for a new device. */
  initialTypeId: PropTypes.number,
  /** Preselected subtype id for edit mode; null for a new device or type with no subtype yet chosen. */
  initialSubtypeId: PropTypes.number,
  label: PropTypes.string,
  typePrompt: PropTypes.string,
  subtypeLabel: PropTypes.string,
  subtypePrompt: PropTypes.string
};

ValveTypePicker.defaultProps = {
  instanceId: null,
  initialTypeId: null,
  initialSubtypeId: null,
  label: null,
  typePrompt: null,
  subtypeLabel: null,
  subtypePrompt: null
};

export default ValveTypePicker;
